
import { useState } from 'react'

const MembershipApplicationForm = () => {
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    nationality: '',
    
    // Contact Information
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    
    // Professional Information
    occupation: '',
    company: '',
    annualIncome: '',
    
    // Style Preferences
    membershipTier: '',
    preferredBrands: [] as string[],
    stylePreferences: [] as string[],
    sizeInformation: {
      tops: '',
      bottoms: '',
      shoes: '',
      dresses: ''
    },
    
    // Lifestyle
    lifestyleEvents: [] as string[],
    travelFrequency: '',
    shoppingBudget: '',
    
    // References
    referenceSource: '',
    referralName: '',
    
    // Agreements
    termsAccepted: false,
    privacyAccepted: false,
    marketingConsent: false
  })
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState('')

  const membershipTiers = [
    { value: 'prestige', label: 'Prestige ($2,500/year)', description: 'Essential luxury services and priority access' },
    { value: 'elite', label: 'Elite ($5,000/year)', description: 'Premium styling with dedicated consultant' },
    { value: 'signature', label: 'Signature ($10,000/year)', description: 'Ultimate bespoke experience with 24/7 concierge' }
  ]

  const brandOptions = [
    'Chanel', 'Louis Vuitton', 'Hermès', 'Gucci', 'Prada', 'Dior', 'Saint Laurent', 'Bottega Veneta',
    'Valentino', 'Givenchy', 'Balenciaga', 'Tom Ford', 'Brunello Cucinelli', 'The Row', 'Other'
  ]

  const stylePreferences = [
    'Classic/Timeless', 'Modern/Contemporary', 'Bohemian', 'Minimalist', 'Avant-garde',
    'Business Professional', 'Casual Luxury', 'Evening/Formal', 'Seasonal Trends'
  ]

  const lifestyleEvents = [
    'Business Meetings', 'Social Events', 'Charity Galas', 'Travel', 'Red Carpet Events',
    'Casual Entertaining', 'Art Openings', 'Fashion Shows', 'Sporting Events'
  ]

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.')
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof typeof prev] as object),
          [child]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      }))
    }
  }

  const handleArrayChange = (name: string, value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [name]: checked 
        ? [...(prev[name as keyof typeof prev] as string[]), value]
        : (prev[name as keyof typeof prev] as string[]).filter(item => item !== value)
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')

    // Client-side validation
    if (!formData.termsAccepted || !formData.privacyAccepted) {
      setError('Please accept the required terms and privacy policy.')
      setIsSubmitting(false)
      return
    }

    try {
      const response = await fetch('/api/v1/membership', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit application');
      }

      setIsSubmitted(true)
    } catch (err) {
      console.error('Submission error:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false)
    }
  }

  const nextStep = () => {
    if (currentStep < 4) setCurrentStep(currentStep + 1)
  }

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1)
  }

  const steps = [
    { number: 1, title: 'Personal Information', description: 'Basic details and contact information' },
    { number: 2, title: 'Style Preferences', description: 'Your fashion style and preferences' },
    { number: 3, title: 'Lifestyle & Budget', description: 'Lifestyle needs and investment level' },
    { number: 4, title: 'Review & Submit', description: 'Final review and application submission' }
  ]

  if (isSubmitted) {
    return (
      <section id="application-form" className="py-20 bg-luxury-cream-50">
        <div className="container-luxury">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-luxury-gold-500 rounded-full flex items-center justify-center mx-auto mb-8">
              <span className="text-3xl text-luxury-black-950">✓</span>
            </div>
            <h2 className="luxury-heading text-3xl mb-6 text-luxury-black-950">
              Application Submitted Successfully
            </h2>
            <p className="luxury-body text-lg text-luxury-black-600 mb-8">
              Thank you for applying to FGA Prestige! Our team will review your application 
              and contact you within 2-3 business days to schedule your consultation.
            </p>
            
            <div className="bg-white p-8 shadow-luxury border-l-4 border-luxury-gold-500 mb-8">
              <h3 className="luxury-subheading text-xl mb-4 text-luxury-black-950">
                What's Next?
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                <div>
                  <h4 className="font-medium text-luxury-black-950 mb-2">Application Review</h4>
                  <p className="text-sm text-luxury-black-600">Our team will evaluate your application and style preferences.</p>
                </div>
                <div>
                  <h4 className="font-medium text-luxury-black-950 mb-2">Consultation Call</h4>
                  <p className="text-sm text-luxury-black-600">We'll schedule a personal consultation to discuss your membership.</p>
                </div>
              </div>
            </div>
            
            <div className="text-sm text-luxury-black-500">
              Application Reference: FGA-{Date.now().toString().slice(-6)}
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section id="application-form" className="py-20 bg-luxury-cream-50">
      <div className="container-luxury">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="luxury-heading text-3xl md:text-4xl mb-6 text-luxury-black-950">
              Membership Application
            </h2>
            <p className="luxury-body text-lg text-luxury-black-600 max-w-2xl mx-auto">
              Complete your application to join our exclusive community of luxury fashion enthusiasts.
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="mb-12">
            <div className="flex justify-between items-center mb-8">
              {steps.map((step) => (
                <div key={step.number} className="flex-1">
                  <div className="flex items-center">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold
                      ${currentStep >= step.number 
                        ? 'bg-luxury-gold-500 text-luxury-black-950' 
                        : 'bg-luxury-black-200 text-luxury-black-500'
                      }
                    `}>
                      {step.number}
                    </div>
                    {step.number < steps.length && (
                      <div className={`
                        flex-1 h-px mx-4
                        ${currentStep > step.number ? 'bg-luxury-gold-500' : 'bg-luxury-black-200'}
                      `} />
                    )}
                  </div>
                  <div className="mt-2 text-center">
                    <div className="text-sm font-medium text-luxury-black-950">{step.title}</div>
                    <div className="text-xs text-luxury-black-600">{step.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="bg-white p-8 md:p-12 shadow-luxury">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <div className="space-y-8">
                  <h3 className="luxury-subheading text-2xl mb-8 text-luxury-black-950">
                    Personal Information
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-luxury-black-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="input-luxury w-full"
                        placeholder="Your first name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-luxury-black-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="input-luxury w-full"
                        placeholder="Your last name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-luxury-black-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="input-luxury w-full"
                        placeholder="your.email@example.com"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-luxury-black-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="input-luxury w-full"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-luxury-black-700 mb-2">
                        Date of Birth *
                      </label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        required
                        className="input-luxury w-full"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-luxury-black-700 mb-2">
                        Nationality
                      </label>
                      <input
                        type="text"
                        name="nationality"
                        value={formData.nationality}
                        onChange={handleInputChange}
                        className="input-luxury w-full"
                        placeholder="Your nationality"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <h4 className="luxury-subheading text-lg text-luxury-black-950">Address Information</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-luxury-black-700 mb-2">
                        Street Address *
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        required
                        className="input-luxury w-full"
                        placeholder="123 Main Street, Apt 4B"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-luxury-black-700 mb-2">
                          City *
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          required
                          className="input-luxury w-full"
                          placeholder="New York"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-luxury-black-700 mb-2">
                          State/Province
                        </label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          className="input-luxury w-full"
                          placeholder="NY"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-luxury-black-700 mb-2">
                          ZIP/Postal Code
                        </label>
                        <input
                          type="text"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleInputChange}
                          className="input-luxury w-full"
                          placeholder="10001"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-luxury-black-700 mb-2">
                          Country *
                        </label>
                        <select
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          required
                          className="input-luxury w-full"
                        >
                          <option value="">Select Country</option>
                          <option value="US">United States</option>
                          <option value="CA">Canada</option>
                          <option value="UK">United Kingdom</option>
                          <option value="FR">France</option>
                          <option value="IT">Italy</option>
                          <option value="DE">Germany</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Style Preferences */}
              {currentStep === 2 && (
                <div className="space-y-8">
                  <h3 className="luxury-subheading text-2xl mb-8 text-luxury-black-950">
                    Style Preferences
                  </h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-luxury-black-700 mb-4">
                      Desired Membership Tier *
                    </label>
                    <div className="space-y-4">
                      {membershipTiers.map((tier) => (
                        <label key={tier.value} className="flex items-start space-x-3 p-4 border border-luxury-black-200 hover:border-luxury-gold-300 transition-colors duration-300">
                          <input
                            type="radio"
                            name="membershipTier"
                            value={tier.value}
                            checked={formData.membershipTier === tier.value}
                            onChange={handleInputChange}
                            className="mt-1 text-luxury-gold-500 focus:ring-luxury-gold-500"
                            required
                          />
                          <div>
                            <div className="font-medium text-luxury-black-950">{tier.label}</div>
                            <div className="text-sm text-luxury-black-600">{tier.description}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-luxury-black-700 mb-4">
                      Preferred Brands (Select all that apply)
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {brandOptions.map((brand) => (
                        <label key={brand} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.preferredBrands.includes(brand)}
                            onChange={(e) => handleArrayChange('preferredBrands', brand, e.target.checked)}
                            className="text-luxury-gold-500 focus:ring-luxury-gold-500"
                          />
                          <span className="text-sm text-luxury-black-700">{brand}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-luxury-black-700 mb-4">
                      Style Preferences (Select all that apply)
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {stylePreferences.map((style) => (
                        <label key={style} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.stylePreferences.includes(style)}
                            onChange={(e) => handleArrayChange('stylePreferences', style, e.target.checked)}
                            className="text-luxury-gold-500 focus:ring-luxury-gold-500"
                          />
                          <span className="text-sm text-luxury-black-700">{style}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="luxury-subheading text-lg mb-4 text-luxury-black-950">Size Information</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(formData.sizeInformation).map(([key, value]) => (
                        <div key={key}>
                          <label className="block text-sm font-medium text-luxury-black-700 mb-2">
                            {key.charAt(0).toUpperCase() + key.slice(1)}
                          </label>
                          <input
                            type="text"
                            name={`sizeInformation.${key}`}
                            value={value}
                            onChange={handleInputChange}
                            className="input-luxury w-full"
                            placeholder={`Your ${key} size`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Lifestyle & Budget */}
              {currentStep === 3 && (
                <div className="space-y-8">
                  <h3 className="luxury-subheading text-2xl mb-8 text-luxury-black-950">
                    Lifestyle & Investment
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-luxury-black-700 mb-2">
                        Occupation
                      </label>
                      <input
                        type="text"
                        name="occupation"
                        value={formData.occupation}
                        onChange={handleInputChange}
                        className="input-luxury w-full"
                        placeholder="Your profession"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-luxury-black-700 mb-2">
                        Company/Organization
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="input-luxury w-full"
                        placeholder="Your company"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-luxury-black-700 mb-4">
                      Lifestyle Events (Select all that apply)
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {lifestyleEvents.map((event) => (
                        <label key={event} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.lifestyleEvents.includes(event)}
                            onChange={(e) => handleArrayChange('lifestyleEvents', event, e.target.checked)}
                            className="text-luxury-gold-500 focus:ring-luxury-gold-500"
                          />
                          <span className="text-sm text-luxury-black-700">{event}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-luxury-black-700 mb-2">
                        Annual Shopping Budget
                      </label>
                      <select
                        name="shoppingBudget"
                        value={formData.shoppingBudget}
                        onChange={handleInputChange}
                        className="input-luxury w-full"
                      >
                        <option value="">Select Budget Range</option>
                        <option value="10k-25k">$10,000 - $25,000</option>
                        <option value="25k-50k">$25,000 - $50,000</option>
                        <option value="50k-100k">$50,000 - $100,000</option>
                        <option value="100k+">$100,000+</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-luxury-black-700 mb-2">
                        Travel Frequency
                      </label>
                      <select
                        name="travelFrequency"
                        value={formData.travelFrequency}
                        onChange={handleInputChange}
                        className="input-luxury w-full"
                      >
                        <option value="">Select Frequency</option>
                        <option value="rarely">Rarely</option>
                        <option value="monthly">Monthly</option>
                        <option value="weekly">Weekly</option>
                        <option value="constantly">Constantly Traveling</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-luxury-black-700 mb-2">
                      How did you hear about FGA Prestige?
                    </label>
                    <select
                      name="referenceSource"
                      value={formData.referenceSource}
                      onChange={handleInputChange}
                      className="input-luxury w-full"
                    >
                      <option value="">Select Source</option>
                      <option value="referral">Personal Referral</option>
                      <option value="social-media">Social Media</option>
                      <option value="search-engine">Search Engine</option>
                      <option value="magazine">Fashion Magazine</option>
                      <option value="event">Fashion Event</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  {formData.referenceSource === 'referral' && (
                    <div>
                      <label className="block text-sm font-medium text-luxury-black-700 mb-2">
                        Referral Name
                      </label>
                      <input
                        type="text"
                        name="referralName"
                        value={formData.referralName}
                        onChange={handleInputChange}
                        className="input-luxury w-full"
                        placeholder="Name of person who referred you"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Step 4: Review & Submit */}
              {currentStep === 4 && (
                <div className="space-y-8">
                  <h3 className="luxury-subheading text-2xl mb-8 text-luxury-black-950">
                    Review & Submit
                  </h3>
                  
                  <div className="bg-luxury-cream-50 p-6 space-y-4">
                    <h4 className="font-medium text-luxury-black-950">Application Summary</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Name:</span> {formData.firstName} {formData.lastName}
                      </div>
                      <div>
                        <span className="font-medium">Email:</span> {formData.email}
                      </div>
                      <div>
                        <span className="font-medium">Membership Tier:</span> {membershipTiers.find(t => t.value === formData.membershipTier)?.label}
                      </div>
                      <div>
                        <span className="font-medium">Location:</span> {formData.city}, {formData.country}
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <label className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        name="termsAccepted"
                        checked={formData.termsAccepted}
                        onChange={handleInputChange}
                        required
                        className="mt-1 text-luxury-gold-500 focus:ring-luxury-gold-500"
                      />
                      <span className="text-sm text-luxury-black-700">
                        I have read and agree to the{' '}
                        <a href="/terms" className="text-luxury-gold-600 hover:text-luxury-gold-700 underline">
                          Terms and Conditions
                        </a>{' '}
                        and{' '}
                        <a href="/privacy-policy" className="text-luxury-gold-600 hover:text-luxury-gold-700 underline">
                          Privacy Policy
                        </a>.
                      </span>
                    </label>
                    
                    <label className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        name="privacyAccepted"
                        checked={formData.privacyAccepted}
                        onChange={handleInputChange}
                        required
                        className="mt-1 text-luxury-gold-500 focus:ring-luxury-gold-500"
                      />
                      <span className="text-sm text-luxury-black-700">
                        I consent to FGA Prestige processing my personal information for membership evaluation and service delivery.
                      </span>
                    </label>
                    
                    <label className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        name="marketingConsent"
                        checked={formData.marketingConsent}
                        onChange={handleInputChange}
                        className="mt-1 text-luxury-gold-500 focus:ring-luxury-gold-500"
                      />
                      <span className="text-sm text-luxury-black-700">
                        I would like to receive updates about exclusive offers, events, and style insights (optional).
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-8 border-t border-luxury-black-200">
                {currentStep > 1 ? (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="btn-luxury-secondary"
                  >
                    Previous Step
                  </button>
                ) : (
                  <div />
                )}
                
                {currentStep < 4 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn-luxury-primary"
                  >
                    Next Step
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting || !formData.termsAccepted || !formData.privacyAccepted}
                    className={`
                      px-8 py-3 font-medium uppercase tracking-wider transition-all duration-300
                      ${isSubmitting || !formData.termsAccepted || !formData.privacyAccepted
                        ? 'bg-luxury-black-300 text-luxury-black-500 cursor-not-allowed' 
                        : 'btn-luxury-primary'
                      }
                    `}
                  >
                    {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  )
}

export default MembershipApplicationForm